import graphql__58a6e4bce56213f68e51 from "./__generated__/AnalysisForecastOverviewViewQuery.graphql.ts";import * as React from "react";
import {startTransition, Suspense, useEffect, useMemo, useState} from "react";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {Link, useOutletContext} from "react-router";
import Panel from "./presentational/Panel";
import Hint from "./presentational/Hint";
import Warning from "./presentational/Warning";
import BalanceLineChart, {BalanceLine} from "./presentational/chart/BalanceLineChart";
import {PROVISIONAL_HATCH_STYLE} from "./presentational/chart/provisional";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import {MonthAmountRow, MonthCell, MonthContentRow, MonthLineRow, MonthTable, monthLabel} from "./AnalysisForecastMonthTable";
import AnalysisForecastMinimumBalance from "./AnalysisForecastMinimumBalance";
import AnalysisForecastPartsPanel, {ForecastLine, readForecastLine} from "./AnalysisForecastPartsPanel";
import {dayToDate, formatDay, monthToDate} from "../helper/dateFormat";
import {formatAmount, formatWholeAmount} from "../helper/revenueFormat";
import AnalysisForecastOverviewViewQueryNode from "./__generated__/AnalysisForecastOverviewViewQuery.graphql";
import type {AnalysisForecastOverviewViewQuery as QueryType, AnalysisForecastOverviewViewQuery$data as QueryData} from "./__generated__/AnalysisForecastOverviewViewQuery.graphql";

type QueryMonth = QueryData['forecast']['months'][number];
type Category = QueryMonth['result']['lines'][number]['category'];
type TransactionGroup = QueryMonth['liquidity']['inflows'][number]['transactionGroup'];

type LiquidityRowKey = {transactionGroup: TransactionGroup, isOverdueReceivables: boolean};

type ResultLine = ForecastLine & {category: Category};
type LiquidityLine = ForecastLine & {transactionGroup: TransactionGroup, isOverdueReceivables: boolean};

/** A month of the forecast with the parts of its lines read from their fragment */
type Month = Omit<QueryMonth, 'result' | 'liquidity'> & {
    result: Omit<QueryMonth['result'], 'lines' | 'bwaComparisonLines'> & {lines: ResultLine[], bwaComparisonLines: ResultLine[]}
    liquidity: Omit<QueryMonth['liquidity'], 'inflows' | 'outflows'> & {inflows: LiquidityLine[], outflows: LiquidityLine[]}
};

/** A month as a column of the liquidity table, which has a status of its own */
type LiquidityColumn = Omit<Month, 'status'> & {status: QueryMonth['liquidity']['status']};

/** The cell the drill-down shows: a line of the result, of the BWA comparison or of the liquidity in a month */
type Selection = {
    table: 'result' | 'bwa'
    monthIndex: number
    category: Category
} | {
    table: 'liquidity'
    monthIndex: number
    transactionGroup: TransactionGroup
    isOverdueReceivables: boolean
    direction: 'inflow' | 'outflow'
};

const RESULT_CATEGORIES: Category[] = ['REVENUE', 'OTHER_OPERATING_INCOME', 'SUPPLIERS', 'PERSONNEL', 'DEPRECIATION', 'OTHER', 'INCOME_TAXES'];
const BWA_COMPARISON_CATEGORIES: Category[] = ['REVENUE', 'SUPPLIERS'];

const TOTAL_COLOR = '#1d4ed8'; // blue-700
const ACCOUNT_COLORS = ['#0d9488', '#d97706', '#7c3aed']; // teal-600, amber-600, violet-600
const MINIMUM_COLOR = '#dc2626'; // red-600

const analysisForecastOverviewViewQuery = graphql__58a6e4bce56213f68e51;

type AnalysisOutletContext = {
    forecastOverviewQuery: any
};

/**
 * The "Overview" area of the forecast: the balance of the bank accounts over the months, the net
 * result by month of service and the gross liquidity by day of payment. Every cell opens the
 * parts it consists of. The forecast takes a few seconds to compute, so the area shows a
 * loading state meanwhile.
 */
const AnalysisForecastOverviewView: React.FC = () => {
    const intl = useIntl();
    return (
        <Suspense fallback={<p className="animate-pulse px-4 py-8 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.loading'})}</p>}>
            <Overview/>
        </Suspense>
    );
};

const Overview: React.FC = () => {
    const intl = useIntl();
    const [selection, setSelection] = useState<Selection | null>(null);

    const {forecastOverviewQuery} = useOutletContext<AnalysisOutletContext>();
    const [queryRef, loadForecastQuery] = useQueryLoader<QueryType>(AnalysisForecastOverviewViewQueryNode, forecastOverviewQuery);
    const data = usePreloadedQuery<QueryType>(analysisForecastOverviewViewQuery, queryRef ?? forecastOverviewQuery);
    const forecast = data.forecast;
    const reloadForecast = () => {
        startTransition(() => {
            loadForecastQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const months: Month[] = useMemo(() => forecast.months.map(month => ({
        ...month,
        result: {...month.result, lines: month.result.lines.map(readForecastLine), bwaComparisonLines: month.result.bwaComparisonLines.map(readForecastLine)},
        liquidity: {
            ...month.liquidity,
            inflows: month.liquidity.inflows.map(readForecastLine),
            outflows: month.liquidity.outflows.map(readForecastLine),
        },
    })), [forecast]);


    const liquidityMonths: LiquidityColumn[] = useMemo(() => months.map(month => ({...month, status: month.liquidity.status})), [months]);

    const minimumBalance = forecast.minimumBalance?.amount ?? null;

    // --- Chart
    const firstForecastIndex = months.findIndex(month => month.liquidity.hasForecastDays);
    const dashedFrom = firstForecastIndex === -1 ? undefined : firstForecastIndex;
    const accounts = new Map<string, string>();
    months.forEach(month => month.liquidity.accountBalances.forEach(balance => accounts.set(balance.bankAccountIdentifier, balance.bankAccountName)));
    const chartLines: BalanceLine[] = [
        {id: 'total', label: intl.formatMessage({id: 'view.analysisForecastOverview.chart.total'}), color: TOTAL_COLOR, strokeWidth: 3, dashedFrom, values: months.map(month => month.liquidity.closingBalance)},
        ...Array.from(accounts.entries()).map(([identifier, name], index) => ({
            id: identifier,
            label: name,
            color: ACCOUNT_COLORS[index % ACCOUNT_COLORS.length],
            strokeWidth: 1.5,
            dashedFrom,
            values: months.map(month => month.liquidity.accountBalances.find(balance => balance.bankAccountIdentifier === identifier)?.closingBalance ?? null),
        })),
    ];

    // --- Drill-down
    const categoryLabel = (category: Category) => intl.formatMessage({id: `forecastCategory.${category}`});
    const groupLabel = (group: TransactionGroup) => intl.formatMessage({id: `bankTransactionGroup.${group}`});

    const selectedMonth = selection ? months[selection.monthIndex] : null;
    const selectedLine: ForecastLine | null = selection && selectedMonth
        ? (selection.table !== 'liquidity'
            ? (selection.table === 'result' ? selectedMonth.result.lines : selectedMonth.result.bwaComparisonLines).find(line => line.category === selection.category)
            : (selection.direction === 'inflow' ? selectedMonth.liquidity.inflows : selectedMonth.liquidity.outflows).find(line => line.transactionGroup === selection.transactionGroup && line.isOverdueReceivables === selection.isOverdueReceivables)
        ) ?? null
        : null;
    const bwaComparisonLabel = (category: Category) => intl.formatMessage({id: `view.analysisForecastOverview.result.bwa.${category}`});
    const liquidityLabel = (group: TransactionGroup, isOverdueReceivables: boolean) =>
        isOverdueReceivables ? intl.formatMessage({id: 'view.analysisForecastOverview.liquidity.overdueReceivables'}) : groupLabel(group);
    const selectedLineLabel = selection === null ? '' : (selection.table === 'liquidity' ? liquidityLabel(selection.transactionGroup, selection.isOverdueReceivables) : (selection.table === 'result' ? categoryLabel(selection.category) : bwaComparisonLabel(selection.category)));

    const resultRow = (category: Category) => (
        <MonthLineRow
            key={`result-${category}`}
            label={categoryLabel(category)}
            months={months}
            lineOf={month => month.result.lines.find(line => line.category === category)}
            selectedMonthIndex={selection?.table === 'result' && selection.category === category ? selection.monthIndex : null}
            onSelect={monthIndex => setSelection({table: 'result', monthIndex, category})}
        />
    );

    const bwaComparisonRow = (category: Category) => (
        <MonthLineRow
            key={`bwa-${category}`}
            label={bwaComparisonLabel(category)}
            muted
            months={months}
            lineOf={month => month.result.bwaComparisonLines.find(line => line.category === category)}
            selectedMonthIndex={selection?.table === 'bwa' && selection.category === category ? selection.monthIndex : null}
            onSelect={monthIndex => setSelection({table: 'bwa', monthIndex, category})}
        />
    );
    const hasBwaComparison = months.some(month => month.result.bwaComparisonLines.length > 0);

    const liquidityRow = ({transactionGroup, isOverdueReceivables}: LiquidityRowKey, direction: 'inflow' | 'outflow') => (
        <MonthLineRow
            key={`liquidity-${direction}-${transactionGroup}-${isOverdueReceivables}`}
            label={(direction === 'inflow' ? '+ ' : '− ') + liquidityLabel(transactionGroup, isOverdueReceivables)}
            months={liquidityMonths}
            lineOf={month => (direction === 'inflow' ? month.liquidity.inflows : month.liquidity.outflows).find(line => line.transactionGroup === transactionGroup && line.isOverdueReceivables === isOverdueReceivables)}
            selectedMonthIndex={selection?.table === 'liquidity' && selection.transactionGroup === transactionGroup && selection.isOverdueReceivables === isOverdueReceivables && selection.direction === direction ? selection.monthIndex : null}
            onSelect={monthIndex => setSelection({table: 'liquidity', monthIndex, transactionGroup, isOverdueReceivables, direction})}
        />
    );

    const amountRow = (key: string, labelId: string, amountOf: (month: Month) => number, bold = false, muted = false) => (
        <MonthAmountRow key={key} label={intl.formatMessage({id: `view.analysisForecastOverview.${labelId}`})} months={months} amountOf={amountOf} bold={bold} muted={muted}/>
    );

    const liquidityAmountRow = (key: string, labelId: string, amountOf: (month: LiquidityColumn) => number, bold = false, muted = false) => (
        <MonthAmountRow key={key} label={intl.formatMessage({id: `view.analysisForecastOverview.${labelId}`})} months={liquidityMonths} amountOf={amountOf} bold={bold} muted={muted}/>
    );

    const usedRows = (linesOf: (month: Month) => readonly LiquidityLine[]) => {
        const rows: LiquidityRowKey[] = [];
        months.forEach(month => linesOf(month).forEach(line => {
            if ((line.amount !== 0 || line.parts.length > 0) && !rows.some(row => row.transactionGroup === line.transactionGroup && row.isOverdueReceivables === line.isOverdueReceivables)) {
                rows.push({transactionGroup: line.transactionGroup, isOverdueReceivables: line.isOverdueReceivables});
            }
        }));
        return rows;
    };
    const inflowRows = usedRows(month => month.liquidity.inflows);
    const outflowRows = usedRows(month => month.liquidity.outflows);
    const hasTransfers = months.some(month => month.liquidity.transferNet !== 0);

    const hasDetails = selectedLine !== null;

    // Escape closes the details
    useEffect(() => {
        if (!hasDetails) {
            return;
        }
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSelection(null);
            }
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [hasDetails]);

    return (
        <div className="flex items-start gap-6">
            <div className="min-w-0 flex-1 space-y-6">
                <Panel>
                    <div className="flex flex-wrap items-start gap-x-12 gap-y-4">
                        <div>
                            <div className="text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.currentBalance'})}</div>
                            <div className={`text-2xl font-semibold tabular-nums ${forecast.balanceAtLastBookingDay < 0 ? 'text-red-600' : 'text-gray-900'}`}>{formatAmount(forecast.balanceAtLastBookingDay)}</div>
                            <div className="text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.asOf'}, {date: formatDay(intl, forecast.lastBookingDay)})}</div>
                        </div>
                        <AnalysisForecastMinimumBalance minimumBalance={minimumBalance} formatAmount={formatAmount} onSaved={reloadForecast}/>
                    </div>
                    {forecast.bankAccounts.length === 0 && (
                        <Hint className="mt-5">
                            {intl.formatMessage(
                                {id: 'view.analysisForecastOverview.noBankAccount'},
                                {link: (chunks) => <Link key="finance" className="text-blue-600 hover:underline" to="/configuration/finance">{chunks}</Link>}
                            )}
                        </Hint>
                    )}
                    {forecast.coverageDiffers && (
                        <Warning className="mt-5">
                            <span className="font-medium text-gray-900">
                                {intl.formatMessage({id: 'view.analysisForecastOverview.accountsDiffer'}, {
                                    accounts: forecast.bankAccounts
                                        .filter(account => account.coveredUntil)
                                        .map(account => intl.formatMessage({id: 'view.analysisForecastOverview.accountsDiffer.account'}, {name: account.bankAccountName, date: formatDay(intl, account.coveredUntil as string)}))
                                        .join(', '),
                                    date: formatDay(intl, forecast.lastBookingDay),
                                })}
                            </span>
                        </Warning>
                    )}
                    {forecast.shortfallDay && (
                        <Warning className="mt-5">
                            <span className="font-medium text-gray-900">
                                {intl.formatMessage({id: 'view.analysisForecastOverview.shortfall'}, {date: formatDay(intl, forecast.shortfallDay)})}
                            </span>
                        </Warning>
                    )}
                    {!forecast.shortfallDay && minimumBalance !== null && (
                        <p className="mt-5 text-sm text-green-700">{intl.formatMessage({id: 'view.analysisForecastOverview.noShortfall'})}</p>
                    )}
                </Panel>

                <Panel headline={intl.formatMessage({id: 'view.analysisForecastOverview.chart.headline'})}>
                    <BalanceLineChart
                        points={months.map(month => ({key: month.month, label: monthLabel(intl, month.month), isProvisional: month.liquidity.status !== 'BANK'}))}
                        lines={chartLines}
                        threshold={minimumBalance === null ? null : {label: intl.formatMessage({id: 'view.analysisForecastOverview.minimumBalance'}), amount: minimumBalance, color: MINIMUM_COLOR}}
                        formatAmount={formatWholeAmount}
                    />
                    <p className="mt-2 text-xs text-gray-500" style={{paddingLeft: 72}}>{intl.formatMessage({id: 'view.analysisForecastOverview.chart.legend'})}</p>
                </Panel>

                <Panel headline={intl.formatMessage({id: 'view.analysisForecastOverview.result.headline'})} fullWidthChildren>
                    <p className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500 sm:px-6">{intl.formatMessage({id: 'view.analysisForecastOverview.result.description'})}</p>
                    <MonthTable months={months}>
                        {RESULT_CATEGORIES.map(resultRow)}
                        {amountRow('before-taxes', 'result.beforeIncomeTaxes', month => month.result.resultBeforeIncomeTaxes, true)}
                        {amountRow('result', 'result.result', month => month.result.result, true)}
                        {hasBwaComparison && BWA_COMPARISON_CATEGORIES.map(bwaComparisonRow)}
                    </MonthTable>
                    <p className="border-t border-gray-200 px-4 py-3 text-xs text-gray-500 sm:px-6">{intl.formatMessage({id: 'view.analysisForecastOverview.result.statusLegend'})}</p>
                    {hasBwaComparison && (
                        <p className="border-t border-gray-200 px-4 py-3 text-xs text-gray-500 sm:px-6">{intl.formatMessage({id: 'view.analysisForecastOverview.result.bwa.description'})}</p>
                    )}
                </Panel>

                <Panel headline={intl.formatMessage({id: 'view.analysisForecastOverview.liquidity.headline'})} fullWidthChildren>
                    <p className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500 sm:px-6">{intl.formatMessage({id: 'view.analysisForecastOverview.liquidity.description'})}</p>
                    <MonthTable months={liquidityMonths}>
                        {liquidityAmountRow('opening', 'liquidity.opening', month => month.liquidity.openingBalance, true)}
                        {inflowRows.map(row => liquidityRow(row, 'inflow'))}
                        {liquidityAmountRow('inflows', 'liquidity.totalInflows', month => month.liquidity.totalInflows, false, true)}
                        {outflowRows.map(row => liquidityRow(row, 'outflow'))}
                        {liquidityAmountRow('outflows', 'liquidity.totalOutflows', month => month.liquidity.totalOutflows, false, true)}
                        {hasTransfers && liquidityAmountRow('transfers', 'liquidity.transfers', month => month.liquidity.transferNet, false, true)}
                        {liquidityAmountRow('closing', 'liquidity.closing', month => month.liquidity.closingBalance, true)}
                        <MonthContentRow label={intl.formatMessage({id: 'view.analysisForecastOverview.liquidity.lowest'})} months={liquidityMonths}>
                            {month => (
                                <MonthCell month={month} amount={month.liquidity.lowestBalance}>
                                    {formatWholeAmount(month.liquidity.lowestBalance)}
                                    <span className="ml-1 text-xs text-gray-500">({intl.formatDate(dayToDate(month.liquidity.lowestBalanceDay), {day: 'numeric', month: 'numeric', timeZone: 'UTC'})})</span>
                                </MonthCell>
                            )}
                        </MonthContentRow>
                    </MonthTable>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-gray-200 px-4 py-3 text-xs text-gray-500 sm:px-6">
                        <span className="flex items-center gap-2">
                            <span className="inline-block h-4 w-8 rounded-sm border border-gray-200" style={PROVISIONAL_HATCH_STYLE}/>
                            <span className="italic">{intl.formatMessage({id: 'view.analysisForecastOverview.legend.liquidityForecast'})}</span>
                        </span>
                        <span>{intl.formatMessage({id: 'view.analysisForecastOverview.legend.drillDown'})}</span>
                    </div>
                </Panel>

                {forecast.doubtfulReceivables.length > 0 && (
                    <Panel headline={intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.headline'})} fullWidthChildren>
                        <p className="border-b border-gray-200 px-4 py-3 text-sm text-gray-500 sm:px-6">{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.description'})}</p>
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 pr-3 sm:pl-6">{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.customer'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.invoice'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.reason'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.overdue'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.amount'})}</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {forecast.doubtfulReceivables.map((receivable, index) => (
                                    <tr key={receivable.invoiceIdentifier} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <TableData className="pl-4 pr-3 sm:pl-6">{receivable.customerName}</TableData>
                                        <TableData className="px-3">
                                            <Link className="text-blue-600 hover:underline" to={`/invoices/${receivable.invoiceIdentifier}`}>{receivable.invoiceNumber ?? receivable.invoiceIdentifier}</Link>
                                        </TableData>
                                        <TableData className="px-3">{intl.formatMessage({id: `view.analysisForecastOverview.doubtful.reason.${receivable.reason}`})}</TableData>
                                        <TableData className="px-3 tabular-nums" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.doubtful.days'}, {count: receivable.daysOverdue})}</TableData>
                                        <TableData className="pl-3 pr-4 tabular-nums sm:pr-6" textAlignment={TextAlignment.Right}><span className="text-gray-900">{formatAmount(receivable.grossAmount)}</span></TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Panel>
                )}

                <Hint>
                    {intl.formatMessage(
                        {id: 'view.analysisForecastOverview.hint'},
                        {link: (chunks) => <Link key="planned-items" className="text-blue-600 hover:underline" to="/analysis/forecast/planned-items">{chunks}</Link>}
                    )}
                </Hint>
            </div>
        {selection && selectedMonth && selectedLine && (
            // Beside the tables on wide screens, over them on narrow ones. The height is the one of the
            // scrolling main area, which lies below the header of 4rem.
            <aside className="fixed bottom-0 right-0 top-16 z-30 w-full max-w-xl overflow-y-auto bg-gray-100 shadow-xl lg:sticky lg:top-0 lg:z-auto lg:h-[calc(100vh-4rem)] lg:w-[34rem] lg:max-w-none lg:shrink-0 lg:shadow-none">
                <AnalysisForecastPartsPanel
                    title={intl.formatMessage(
                        {id: 'view.analysisForecastOverview.parts.headline'},
                        {
                            table: intl.formatMessage({id: `view.analysisForecastOverview.parts.${selection.table}`}),
                            line: selectedLineLabel,
                            month: intl.formatDate(monthToDate(selectedMonth.month), {month: 'long', year: 'numeric', timeZone: 'UTC'}),
                        }
                    )}
                    line={selectedLine}
                    month={selectedMonth.month}
                    isResult={selection.table !== 'liquidity'}
                    lastBookingDay={forecast.lastBookingDay}
                    onClose={() => setSelection(null)}
                />
            </aside>
        )}
        </div>
    );
};

export default AnalysisForecastOverviewView;
